/**
 * Iter 122 · Admin · Tienda online (config + pedidos)
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Store, Save, Package, ExternalLink, Truck, Eye, Check, MessageSquareWarning, X, FileText } from "lucide-react";

export default function AdminTiendaPegatinas() {
  const [tab, setTab] = useState("config");
  const [cfg, setCfg] = useState(null);
  const [materialesCat, setMaterialesCat] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [detailPid, setDetailPid] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/tienda/config");
    setCfg(r.data.config);
    setMaterialesCat(r.data.materiales_catalogo || []);
    const p = await api.get("/admin/tienda/pedidos");
    setPedidos(p.data || []);
  };

  useEffect(() => { load(); }, []);

  const updateMaterialConfig = (mid, patch) => {
    setCfg((c) => {
      const materiales = { ...(c.materiales || {}) };
      materiales[mid] = { ...(materiales[mid] || {}), ...patch };
      return { ...c, materiales };
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = {
        activa: cfg.activa,
        iva_pct: Number(cfg.iva_pct),
        pvp_multiplier_default: Number(cfg.pvp_multiplier_default),
        min_pedido_eur: Number(cfg.min_pedido_eur),
        gastos_envio_eur: Number(cfg.gastos_envio_eur),
        gastos_envio_gratis_desde: Number(cfg.gastos_envio_gratis_desde),
        titulo: cfg.titulo,
        subtitulo: cfg.subtitulo,
        aviso_legal: cfg.aviso_legal,
        materiales: cfg.materiales || {},
      };
      await api.put("/admin/tienda/config", payload);
      toast.success("Configuración guardada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    }
    setSaving(false);
  };

  const marcarEnviado = async (pid) => {
    if (!window.confirm("¿Marcar este pedido como enviado?")) return;
    try {
      await api.post(`/admin/tienda/pedidos/${pid}/marcar-enviado`);
      toast.success("Marcado como enviado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const convertirPeticion = async (pid) => {
    if (!window.confirm("¿Convertir este pedido en una petición DNF?")) return;
    try {
      const r = await api.post(`/admin/tienda/pedidos/${pid}/convertir-peticion`);
      toast.success(`Petición ${r.data.peticion_codigo} creada`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  if (!cfg) return <div className="p-6 text-zinc-400">Cargando…</div>;

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <PageHeader
        icon={Store}
        title="Tienda online · Pegatinas"
        description="Configura los materiales visibles, márgenes y ve los pedidos que llegan del sitio público."
        actions={
          <Button asChild variant="outline" size="sm" data-testid="tienda-preview-link">
            <a href="/tienda/pegatinas" target="_blank" rel="noreferrer">
              <ExternalLink className="w-4 h-4 mr-1" /> Ver tienda
            </a>
          </Button>
        }
      />

      <div className="flex gap-2 mt-4 border-b border-zinc-800">
        <TabBtn active={tab === "config"} onClick={() => setTab("config")} testId="tab-config">Configuración</TabBtn>
        <TabBtn active={tab === "pedidos"} onClick={() => setTab("pedidos")} testId="tab-pedidos">
          Pedidos ({pedidos.length})
        </TabBtn>
      </div>

      {tab === "config" ? (
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardContent className="p-4 space-y-3">
              <h3 className="font-semibold text-sm">General</h3>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!cfg.activa}
                  onChange={(e) => setCfg({ ...cfg, activa: e.target.checked })}
                  data-testid="cfg-activa" />
                Tienda activa (visible para clientes)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <F label="IVA %"><Input type="number" step="0.1" value={cfg.iva_pct}
                  onChange={(e) => setCfg({ ...cfg, iva_pct: e.target.value })} data-testid="cfg-iva" /></F>
                <F label="Margen PVP × (default)"><Input type="number" step="0.1" value={cfg.pvp_multiplier_default}
                  onChange={(e) => setCfg({ ...cfg, pvp_multiplier_default: e.target.value })} data-testid="cfg-margen-default" /></F>
                <F label="Pedido mínimo (€, sin IVA)"><Input type="number" step="1" value={cfg.min_pedido_eur}
                  onChange={(e) => setCfg({ ...cfg, min_pedido_eur: e.target.value })} data-testid="cfg-min-pedido" /></F>
                <F label="Gastos envío (€)"><Input type="number" step="0.1" value={cfg.gastos_envio_eur}
                  onChange={(e) => setCfg({ ...cfg, gastos_envio_eur: e.target.value })} data-testid="cfg-envio" /></F>
                <F label="Envío gratis desde (€, sin IVA)"><Input type="number" step="1" value={cfg.gastos_envio_gratis_desde}
                  onChange={(e) => setCfg({ ...cfg, gastos_envio_gratis_desde: e.target.value })} data-testid="cfg-envio-gratis" /></F>
              </div>
              <div className="border-t border-zinc-800 pt-3 space-y-2">
                <F label="Título landing"><Input value={cfg.titulo || ""}
                  onChange={(e) => setCfg({ ...cfg, titulo: e.target.value })} data-testid="cfg-titulo" /></F>
                <F label="Subtítulo"><Input value={cfg.subtitulo || ""}
                  onChange={(e) => setCfg({ ...cfg, subtitulo: e.target.value })} /></F>
                <F label="Aviso legal (bajo formulario)"><Textarea rows={2} value={cfg.aviso_legal || ""}
                  onChange={(e) => setCfg({ ...cfg, aviso_legal: e.target.value })} /></F>
              </div>
              <Button onClick={guardar} disabled={saving}
                className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="cfg-guardar">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : "Guardar configuración"}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardContent className="p-4 space-y-2">
              <h3 className="font-semibold text-sm">Materiales visibles al cliente</h3>
              <p className="text-xs text-zinc-400">
                Marca los materiales que se muestran en la tienda. Si dejas vacío el multiplicador, se usa el default ({cfg.pvp_multiplier_default}×).
              </p>
              <div className="space-y-1.5 mt-2 max-h-[520px] overflow-auto pr-1">
                {materialesCat.map((m) => {
                  const cur = cfg.materiales?.[m.id] || {};
                  const mult = cur.multiplier ?? cfg.pvp_multiplier_default;
                  const pvp = (m.coste_m2 * Number(mult || 1)).toFixed(2);
                  return (
                    <div key={m.id} className="flex items-center gap-2 bg-zinc-950/70 border border-zinc-800 rounded p-2 text-xs">
                      <label className="flex items-center gap-1.5 min-w-0 flex-1 cursor-pointer">
                        <input type="checkbox" checked={!!cur.visible}
                          onChange={(e) => updateMaterialConfig(m.id, { visible: e.target.checked })}
                          data-testid={`cfg-mat-visible-${m.id}`} />
                        <div className="min-w-0">
                          <div className="truncate font-medium">{m.nombre}</div>
                          <div className="text-[10px] text-zinc-500">Coste: {m.coste_m2.toFixed(2)}€/m²</div>
                        </div>
                      </label>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-zinc-500">×</span>
                        <Input type="number" step="0.1" min="1" value={cur.multiplier ?? ""}
                          placeholder={String(cfg.pvp_multiplier_default)}
                          onChange={(e) => updateMaterialConfig(m.id, { multiplier: e.target.value === "" ? null : Number(e.target.value) })}
                          className="h-7 w-16 text-xs" data-testid={`cfg-mat-mult-${m.id}`} />
                        <span className="text-[10px] text-emerald-400 w-14 text-right">= {pvp}€</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="mt-4">
          {pedidos.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm">Aún no hay pedidos.</div>
          ) : (
            <div className="overflow-auto rounded-lg border border-zinc-800">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-xs text-zinc-400">
                  <tr>
                    <th className="text-left px-3 py-2">Order</th>
                    <th className="text-left px-3 py-2">Fecha</th>
                    <th className="text-left px-3 py-2">Cliente</th>
                    <th className="text-left px-3 py-2">Material</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-center px-3 py-2">Estado</th>
                    <th className="text-right px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidos.map((p) => (
                    <tr key={p.id} className="border-t border-zinc-800 hover:bg-zinc-900/50" data-testid={`pedido-${p.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{p.order_redsys}</td>
                      <td className="px-3 py-2 text-xs text-zinc-400">
                        {new Date(p.created_at).toLocaleString("es-ES")}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs">{p.direccion?.nombre}</div>
                        <div className="text-[10px] text-zinc-500">{p.direccion?.email}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {p.material_nombre}<br />
                        <span className="text-zinc-500">{p.desglose_pvp?.cantidad_total} uds</span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">{Number(p.importe_total).toFixed(2)}€</td>
                      <td className="px-3 py-2 text-center"><EstadoBadge estado={p.estado} /></td>
                      <td className="px-3 py-2 text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => setDetailPid(p.id)}
                          data-testid={`ver-${p.id}`}>
                          <Eye className="w-3 h-3 mr-1" /> Revisar
                        </Button>
                        {(p.estado === "pagado" || p.estado === "revision_aprobada") && (
                          <>
                            {!p.peticion_id && (
                              <Button size="sm" variant="outline" onClick={() => convertirPeticion(p.id)}
                                data-testid={`convertir-${p.id}`}>
                                A petición
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => marcarEnviado(p.id)}
                              data-testid={`enviar-${p.id}`}>
                              <Truck className="w-3 h-3 mr-1" /> Enviado
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {detailPid && (
        <PedidoDetalleModal
          pid={detailPid}
          onClose={() => setDetailPid(null)}
          onChanged={() => { setDetailPid(null); load(); }}
        />
      )}
    </div>
  );
}

// ================ Modal detalle + revisión de diseño ================
function PedidoDetalleModal({ pid, onClose, onChanged }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notasCambios, setNotasCambios] = useState("");
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [showCambios, setShowCambios] = useState(false);
  const [showRechazo, setShowRechazo] = useState(false);

  useEffect(() => {
    api.get(`/admin/tienda/pedidos/${pid}`).then((r) => setP(r.data));
  }, [pid]);

  const aprobar = async () => {
    if (!window.confirm("¿Aprobar el diseño? El cliente recibirá un email de confirmación.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/tienda/pedidos/${pid}/aprobar-diseno`);
      toast.success("Diseño aprobado. Pedido listo para producción.");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const pedirCambios = async () => {
    if (!notasCambios.trim()) return toast.error("Indica qué cambios necesita el diseño");
    setBusy(true);
    try {
      await api.post(`/admin/tienda/pedidos/${pid}/solicitar-cambios`, { notas: notasCambios });
      toast.success("Cambios solicitados. Email enviado al cliente.");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const rechazar = async () => {
    if (!motivoRechazo.trim()) return toast.error("Indica el motivo del rechazo");
    if (!window.confirm(`Se REEMBOLSARÁ ${p.importe_total}€ al cliente y se cancelará el pedido. ¿Confirmas?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/tienda/pedidos/${pid}/rechazar-y-reembolsar`, { motivo: motivoRechazo });
      toast.success(`Reembolsados ${r.data.importe_reembolsado}€`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  if (!p) return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="text-zinc-400">Cargando…</div>
    </div>
  );

  const enRevision = ["en_revision_diseno", "revision_cambios"].includes(p.estado);
  const files = p.arte_files || [];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="pedido-detalle-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-w-3xl w-full max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div>
            <div className="font-semibold">Pedido {p.order_redsys}</div>
            <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
              <EstadoBadge estado={p.estado} />
              <span>· {Number(p.importe_total).toFixed(2)}€</span>
              <span>· {new Date(p.created_at).toLocaleString("es-ES")}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info k="Cliente" v={p.direccion?.nombre} />
            <Info k="Email" v={p.direccion?.email} />
            <Info k="Teléfono" v={p.direccion?.telefono} />
            <Info k="NIF" v={p.direccion?.nif || "—"} />
            <Info k="Dirección" v={`${p.direccion?.direccion}, ${p.direccion?.cp} ${p.direccion?.poblacion} (${p.direccion?.provincia})`} span2 />
            {p.direccion?.notas && <Info k="Notas cliente" v={p.direccion.notas} span2 />}
            <Info k="Material" v={p.material_nombre} />
            <Info k="Cantidad total" v={`${p.desglose_pvp?.cantidad_total} uds`} />
          </div>

          <div className="border-t border-zinc-800 pt-3">
            <div className="text-xs font-semibold text-zinc-400 mb-2">DISEÑOS</div>
            {p.disenos?.map((d, i) => (
              <div key={i} className="text-sm bg-zinc-900/50 rounded p-2 mb-1">
                <b>{d.nombre}</b> · {d.ancho_cm}×{d.alto_cm}cm × {d.cantidad}u
                {d.corte_unitario && <span className="text-amber-400 text-xs ml-2">✂ cortadas 1×1</span>}
              </div>
            ))}
          </div>

          {/* Archivos del cliente */}
          <div className="border-t border-zinc-800 pt-3">
            <div className="text-xs font-semibold text-zinc-400 mb-2">ARCHIVOS DE ARTE DEL CLIENTE ({files.length})</div>
            {files.length === 0 ? (
              <div className="text-xs text-zinc-500 italic">Aún no ha subido nada.</div>
            ) : (
              <div className="space-y-1">
                {files.map((a, i) => (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded p-2 text-sm hover:border-fuchsia-500"
                    data-testid={`admin-arte-file-${i}`}>
                    <FileText className="w-4 h-4 text-fuchsia-400" />
                    <span className="flex-1 truncate">{a.filename}</span>
                    <span className="text-[10px] text-zinc-500">
                      {new Date(a.uploaded_at).toLocaleString("es-ES")}
                    </span>
                    <span className="text-[10px] text-fuchsia-400">Ver ↗</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {p.notas_disenadora && (
            <div className="bg-amber-900/20 border border-amber-800 rounded p-2 text-xs">
              <div className="font-semibold text-amber-300 mb-1">Notas enviadas al cliente:</div>
              <div className="text-amber-100 whitespace-pre-wrap">{p.notas_disenadora}</div>
            </div>
          )}

          {p.motivo_cancelacion && (
            <div className="bg-red-900/20 border border-red-800 rounded p-2 text-xs">
              <div className="font-semibold text-red-300 mb-1">Motivo cancelación:</div>
              <div className="text-red-100 whitespace-pre-wrap">{p.motivo_cancelacion}</div>
            </div>
          )}

          {/* Acciones de revisión */}
          {enRevision && files.length > 0 && (
            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <div className="text-xs font-semibold text-zinc-400">ACCIONES DE REVISIÓN</div>
              {!showCambios && !showRechazo && (
                <div className="flex flex-wrap gap-2">
                  <Button onClick={aprobar} disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-aprobar-diseno">
                    <Check className="w-4 h-4 mr-1" /> Aprobar diseño
                  </Button>
                  <Button variant="outline" onClick={() => setShowCambios(true)} data-testid="btn-solicitar-cambios">
                    <MessageSquareWarning className="w-4 h-4 mr-1" /> Solicitar cambios
                  </Button>
                  <Button variant="outline" onClick={() => setShowRechazo(true)}
                    className="text-red-400 border-red-800 hover:bg-red-900/30"
                    data-testid="btn-rechazar-reembolsar">
                    <X className="w-4 h-4 mr-1" /> Cancelar + reembolsar
                  </Button>
                </div>
              )}
              {showCambios && (
                <div className="bg-zinc-900 border border-orange-800 rounded p-3 space-y-2">
                  <Label className="text-xs">Qué necesita el cliente cambiar</Label>
                  <Textarea rows={4} value={notasCambios} onChange={(e) => setNotasCambios(e.target.value)}
                    placeholder="Ej: El logo está en baja resolución. Envíanos un archivo vectorial (SVG/AI) para poder imprimirlo con calidad."
                    className="bg-zinc-950 border-zinc-800" data-testid="input-notas-cambios" />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setShowCambios(false)}>Cancelar</Button>
                    <Button onClick={pedirCambios} disabled={busy}
                      className="bg-orange-600 hover:bg-orange-700" data-testid="btn-enviar-cambios">
                      Enviar solicitud al cliente
                    </Button>
                  </div>
                </div>
              )}
              {showRechazo && (
                <div className="bg-zinc-900 border border-red-800 rounded p-3 space-y-2">
                  <Label className="text-xs text-red-300">Motivo del rechazo (se enviará al cliente)</Label>
                  <Textarea rows={4} value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)}
                    placeholder="Ej: Tras dos intentos de ajuste no es viable producir esta pieza con la calidad que exigimos. Procedemos a reembolsar."
                    className="bg-zinc-950 border-zinc-800" data-testid="input-motivo-rechazo" />
                  <div className="text-[11px] text-red-300">
                    ⚠ Se hará un reembolso automático de <b>{Number(p.importe_total).toFixed(2)}€</b> vía Redsys.
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setShowRechazo(false)}>Cancelar</Button>
                    <Button onClick={rechazar} disabled={busy}
                      className="bg-red-600 hover:bg-red-700" data-testid="btn-confirmar-rechazo">
                      Cancelar pedido y reembolsar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {enRevision && files.length === 0 && (
            <div className="border-t border-zinc-800 pt-3 text-xs text-amber-400">
              El cliente aún no ha subido ningún archivo. Puedes esperar o cancelar el pedido si ha pasado mucho tiempo.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({ k, v, span2 }) => (
  <div className={span2 ? "col-span-2" : ""}>
    <div className="text-[10px] text-zinc-500 uppercase">{k}</div>
    <div className="text-sm text-zinc-100">{v}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
      active
        ? "border-fuchsia-500 text-white"
        : "border-transparent text-zinc-400 hover:text-zinc-200"
    }`}>
    {children}
  </button>
);

const F = ({ label, children }) => (
  <div>
    <Label className="text-xs text-zinc-400">{label}</Label>
    {children}
  </div>
);

const EstadoBadge = ({ estado }) => {
  const map = {
    pendiente_pago: "bg-amber-600/20 text-amber-300",
    en_revision_diseno: "bg-fuchsia-600/20 text-fuchsia-300",
    revision_cambios: "bg-orange-600/20 text-orange-300",
    revision_aprobada: "bg-teal-600/20 text-teal-300",
    pagado: "bg-emerald-600/20 text-emerald-300",
    fallido: "bg-red-600/20 text-red-300",
    enviado: "bg-blue-600/20 text-blue-300",
    cancelado: "bg-zinc-600/30 text-zinc-300",
    cancelado_reembolsado: "bg-rose-600/20 text-rose-300",
    reembolso_fallido: "bg-red-800/40 text-red-200",
  };
  const label = {
    pendiente_pago: "PENDIENTE PAGO",
    en_revision_diseno: "REVISIÓN DISEÑO",
    revision_cambios: "CAMBIOS SOLICITADOS",
    revision_aprobada: "DISEÑO APROBADO",
    cancelado_reembolsado: "REEMBOLSADO",
    reembolso_fallido: "REEMBOLSO FALLIDO",
  }[estado] || estado;
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${map[estado] || "bg-zinc-700"}`}>
      {label}
    </span>
  );
};

// unused import silence
void Package;
